import { useState } from 'react';
import { useController, useWatch, type Control } from 'react-hook-form';
import { FieldLabel } from '@/components/forms/FieldLabel';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import type { ContactPreference } from '../../api/partner-profile.api';
import { PARTNER_PROFILE_STRINGS as S } from './partner-profile.strings';
import type { PartnerProfileFormValues } from './schema';

const OPTIONS = [
  { value: 'email', label: S.CONTACT_PREFERENCE_EMAIL },
  { value: 'scheduling', label: S.CONTACT_PREFERENCE_SCHEDULING },
] as const;

interface ContactPreferenceFieldProps {
  control: Control<PartnerProfileFormValues>;
  /** A form-level error for this field (the schema's backstop), shown when the live check has none. */
  error?: string;
}

/**
 * "How should members reach you?" (FR-108): a two-option control, Inquiry email
 * (default, always on file) or Scheduling link. Choosing scheduling is refused until
 * a Scheduling link is entered, with "Add your scheduling link first" shown at once;
 * the same message reappears if the link is cleared while scheduling is selected.
 * The Backend enforces the same rule (PARTNER_SCHEDULING_URL_REQUIRED).
 */
export function ContactPreferenceField({ control, error }: ContactPreferenceFieldProps) {
  const { field } = useController({ control, name: 'contactPreference' });
  const schedulingUrl = useWatch({ control, name: 'schedulingUrl' });
  const [blocked, setBlocked] = useState(false);

  const hasLink = Boolean(schedulingUrl?.trim());
  const needsLink = !hasLink && (blocked || field.value === 'scheduling');

  const handleChange = (next: string) => {
    if (next === 'scheduling' && !hasLink) {
      setBlocked(true);
      return;
    }
    setBlocked(false);
    field.onChange(next as ContactPreference);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel label={S.CONTACT_PREFERENCE_LABEL} variant="caps" />
      <SegmentedControl
        label={S.CONTACT_PREFERENCE_LABEL}
        options={OPTIONS}
        value={field.value}
        onChange={handleChange}
        error={needsLink ? S.SCHEDULING_REQUIRED : error}
      />
      <p className="pl-1 text-p6 text-slate">{S.CONTACT_PREFERENCE_HINT}</p>
    </div>
  );
}
